'use client';

import { useEffect } from 'react';
import type { UseEmblaCarouselType } from 'embla-carousel-react';

type EmblaApi = UseEmblaCarouselType[1];

// embla-carousel-auto-height snapshots slide heights at init and only re-measures on reInit,
// but Embla's built-in resize watcher compares slide WIDTH only. A height-only change (web-font
// swap, rich-text reflow, late-sized media) would therefore leave a stale container height and
// clip the slide. Watch slide heights directly and reInit so the plugin re-measures.
export function useCarouselAutoHeight(emblaApi: EmblaApi, enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !emblaApi) return;

    const slides = emblaApi.slideNodes();
    if (!slides.length) return;

    // Only slides are observed. The container carries the inline height the plugin writes, so
    // observing it would re-enter this callback on every slide change.
    let heights = slides.map((slide) => slide.getBoundingClientRect().height);

    const observer = new ResizeObserver(() => {
      const next = slides.map((slide) => slide.getBoundingClientRect().height);
      // Sub-pixel tolerance mirrors Embla's own resize threshold and avoids reInit churn
      const changed = next.some((height, index) => Math.abs(height - heights[index]) >= 0.5);
      if (!changed) return;
      heights = next;
      emblaApi.reInit();
    });

    slides.forEach((slide) => observer.observe(slide));
    return () => observer.disconnect();
  }, [emblaApi, enabled]);
}
